import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "./lib/admin";
import { DEFAULT_PLAN_FEATURES, imageProxyUrl, normalizePlanFeatures, originalImageUrl, PLAN_FEATURES } from "./lib/stickers";
import { supabase } from "./lib/supabase";
import BrandLogo from "./components/BrandLogo";

const DEFAULT_SETTINGS = {
  brand_name: "PackFigure",
  tagline: "Figurinhas que expressam você",
  accent: "#FF2878",
  logo_url: "",
  banner_title: "Criatividade em cada Story.",
  banner_subtitle: "Mais expressão para deixar seus conteúdos ainda mais especiais.",
  support_email: "nflrailan1@gmail.com",
  support_whatsapp: "5577998295988",
  checkout_default: "https://packfigure.site/#oferta",
  checkout_mensal: "https://packfigure.site/#oferta",
  checkout_trimestral: "https://checkout.wiven.com.br/checkout/cmv5qxelh2w3z01pss0zpr1mu?offer=VNKHM0I",
  checkout_semestral: "https://packfigure.site/#oferta",
  checkout_anual: "https://packfigure.site/#oferta",
  checkout_vitalicio: "https://checkout.wiven.com.br/checkout/cmv5qxelh2w3z01pss0zpr1mu?offer=DVZCGXY",
  wiven_offer_trimestral: "",
  wiven_offer_vitalicio: "",
  plan_features: DEFAULT_PLAN_FEATURES,
};

function normalizeSettings(data = {}) {
  const next = { ...DEFAULT_SETTINGS, ...data };
  next.plan_features = normalizePlanFeatures(data.plan_features);
  next.support_email = DEFAULT_SETTINGS.support_email;
  next.support_whatsapp = DEFAULT_SETTINGS.support_whatsapp;
  next.checkout_default = DEFAULT_SETTINGS.checkout_default;
  next.checkout_mensal = DEFAULT_SETTINGS.checkout_mensal;
  next.checkout_trimestral = DEFAULT_SETTINGS.checkout_trimestral;
  next.checkout_semestral = DEFAULT_SETTINGS.checkout_semestral;
  next.checkout_anual = DEFAULT_SETTINGS.checkout_anual;
  next.checkout_vitalicio = DEFAULT_SETTINGS.checkout_vitalicio;
  if (String(data.brand_name || "").trim().toLowerCase() !== "packfigure") next.brand_name = DEFAULT_SETTINGS.brand_name;
  if (!data.tagline || data.tagline === "um álbum inteiro no seu bolso") next.tagline = DEFAULT_SETTINGS.tagline;
  if (["#ff3d7f", "#ef4c83"].includes(String(data.accent || "").toLowerCase())) next.accent = DEFAULT_SETTINGS.accent;
  if (!data.banner_title || data.banner_title === "Escolha, copie e deixe seu Story com a sua cara.") next.banner_title = DEFAULT_SETTINGS.banner_title;
  if (!data.banner_subtitle || data.banner_subtitle === "Novas figurinhas para transformar seus conteúdos.") next.banner_subtitle = DEFAULT_SETTINGS.banner_subtitle;
  return next;
}

function AdminToast({ message, kind = "success" }) {
  return <div className={`admin-toast ${message ? "visible" : ""} ${kind}`}>{message}</div>;
}

function Metric({ value = 0, label, note, tone }) {
  return <article className={`admin-metric ${tone || ""}`}><span>{label}</span><strong>{Number(value).toLocaleString("pt-BR")}</strong><small>{note}</small></article>;
}

function SetupMessage({ message }) {
  return (
    <section className="admin-setup">
      <span>⚙</span>
      <div><h2>Última etapa de segurança</h2><p>{message}</p><small>O painel já está pronto. Falta conectar a chave privada do Supabase ao Cloudflare e executar o arquivo de configuração do banco.</small></div>
    </section>
  );
}

export default function AdminPage({ profile }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "payments" ? "payments" : "dashboard");
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [setupError, setSetupError] = useState("");
  const [toast, setToast] = useState({ message: "", kind: "success" });

  function notify(message, kind = "success") {
    setToast({ message, kind });
    window.setTimeout(() => setToast({ message: "", kind }), 3000);
  }

  async function loadDashboard() {
    setLoading(true);
    try {
      const [metrics, siteSettings] = await Promise.all([adminApi.get("summary"), adminApi.get("settings")]);
      setSummary(metrics);
      setSettings(normalizeSettings(siteSettings));
      setSetupError("");
    } catch (error) {
      setSetupError(error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDashboard(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmail = params.get("gmail");
    if (!gmail) return;
    setTab("payments");
    notify(gmail === "connected" ? "Gmail conectado. O envio de acessos por e-mail está ativo." : "Não foi possível conectar o Gmail. Confira as credenciais e tente novamente.", gmail === "connected" ? "success" : "error");
    window.history.replaceState({}, "", `${window.location.pathname}?tab=payments`);
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/login");
  }

  if (!profile?.is_admin) {
    return <main className="admin-denied"><span>◆</span><h1>Acesso restrito</h1><p>Sua conta não está marcada como super administradora.</p><Link to="/">Voltar ao aplicativo</Link></main>;
  }

  const tabs = [
    ["dashboard", "Visão geral", "⌂"],
    ["users", "Usuários", "◎"],
    ["content", "Conteúdo", "▦"],
    ["plans", "Planos", "◆"],
    ["appearance", "Site e marca", "✦"],
    ["payments", "Pagamentos", "◇"],
  ];

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-brand"><BrandLogo showTagline={false} /><small>Super Admin</small></div>
        <nav>{tabs.map(([id, label, icon]) => <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}><span>{icon}</span>{label}</button>)}</nav>
        <div className="admin-sidebar-footer"><Link to="/">← Ver aplicativo</Link><button onClick={signOut}>Sair</button></div>
      </aside>
      <main className="admin-main">
        <header className="admin-topbar"><div><p>Central de controle</p><h1>{tabs.find(([id]) => id === tab)?.[1]}</h1></div><div className="admin-user"><span>{(profile.nome || "A").charAt(0).toUpperCase()}</span><div><strong>{profile.nome || "Administrador"}</strong><small>Acesso total</small></div></div></header>
        {setupError && <SetupMessage message={setupError} />}
        {tab === "dashboard" && <Dashboard summary={summary} loading={loading} onRefresh={loadDashboard} />}
        {tab === "users" && <UsersManager settings={settings} notify={notify} />}
        {tab === "content" && <ContentManager notify={notify} />}
        {tab === "plans" && <PlansManager settings={settings} setSettings={setSettings} notify={notify} />}
        {tab === "appearance" && <SettingsForm mode="appearance" settings={settings} setSettings={setSettings} notify={notify} />}
        {tab === "payments" && <SettingsForm mode="payments" settings={settings} setSettings={setSettings} notify={notify} />}
      </main>
      <AdminToast {...toast} />
    </div>
  );
}

function Dashboard({ summary, loading, onRefresh }) {
  return (
    <section className="admin-section">
      <div className="admin-section-heading"><div><h2>Seu negócio em um olhar</h2><p>Dados atuais do acervo e das contas.</p></div><button className="admin-secondary" onClick={onRefresh}>Atualizar</button></div>
      <div className="admin-metrics">
        <Metric value={summary?.users} label="Usuários" note={`${summary?.activeUsers || 0} ativos`} tone="pink" />
        <Metric value={summary?.stickers} label="Figurinhas" note={`${summary?.activeStickers || 0} publicadas`} tone="yellow" />
        <Metric value={summary?.themes} label="Temas" note={`${summary?.categories || 0} categorias`} tone="blue" />
        <Metric value={summary?.admins} label="Administradores" note="com acesso total" tone="green" />
      </div>
      <div className="admin-welcome"><div><p>PAINEL COMPLETO</p><h2>{loading ? "Carregando seus dados…" : "Tudo sob seu controle."}</h2><span>Gerencie contas, acervo, identidade visual e checkouts sem editar código.</span></div><b>✦</b></div>
    </section>
  );
}

const EMPTY_USER = { nome: "", email: "", password: "", plano: "trimestral", expira_em: "", ativo: true, is_admin: false, tem_fornecedores: false, acessos: null, acessos_personalizados: false };

function UsersManager({ settings, notify }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try { setItems(await adminApi.get("users")); } catch (error) { notify(error.message, "error"); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => items.filter((item) => `${item.nome || ""} ${item.email || ""}`.toLowerCase().includes(query.toLowerCase())), [items, query]);

  function open(item) {
    const hasCustomAccess = Boolean(item?.acessos && typeof item.acessos === "object" && !Array.isArray(item.acessos));
    setEditing(item || {});
    setForm(item ? { ...EMPTY_USER, ...item, password: "", expira_em: item.expira_em ? item.expira_em.slice(0, 10) : "", acessos_personalizados: hasCustomAccess } : { ...EMPTY_USER });
  }

  const planAccess = normalizePlanFeatures(settings?.plan_features)[form.plano] || DEFAULT_PLAN_FEATURES.trimestral;
  const effectiveAccess = form.acessos_personalizados ? { ...planAccess, ...(form.acessos || {}) } : planAccess;

  function changePlan(plan) {
    const nextPlanAccess = normalizePlanFeatures(settings?.plan_features)[plan] || DEFAULT_PLAN_FEATURES.trimestral;
    setForm((current) => ({ ...current, plano: plan, acessos: current.acessos_personalizados ? { ...nextPlanAccess } : null }));
  }

  function setCustomAccess(enabled) {
    setForm((current) => ({ ...current, acessos_personalizados: enabled, acessos: enabled ? { ...planAccess } : null }));
  }

  function toggleUserFeature(feature) {
    setForm((current) => ({ ...current, acessos: { ...planAccess, ...(current.acessos || {}), [feature]: !effectiveAccess[feature] } }));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, acessos: form.acessos_personalizados ? effectiveAccess : null, expira_em: form.plano === "vitalicio" ? null : form.expira_em ? new Date(`${form.expira_em}T23:59:59`).toISOString() : null };
      delete payload.acessos_personalizados;
      if (!payload.password) delete payload.password;
      if (editing?.id) await adminApi.patch(`users/${editing.id}`, payload); else await adminApi.post("users", payload);
      notify(editing?.id ? "Usuário atualizado." : "Usuário criado e confirmado.");
      setEditing(null);
      await load();
    } catch (error) { notify(error.message, "error"); } finally { setBusy(false); }
  }

  async function remove(item) {
    if (!window.confirm(`Excluir definitivamente a conta de ${item.nome || item.email}?`)) return;
    try { await adminApi.remove(`users/${item.id}`); notify("Usuário excluído."); await load(); } catch (error) { notify(error.message, "error"); }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading"><div><h2>Contas e acessos</h2><p>Crie, edite, bloqueie ou remova usuários.</p></div><button className="admin-primary" onClick={() => open(null)}>+ Novo usuário</button></div>
      <label className="admin-search"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nome ou e-mail" /></label>
      <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Usuário</th><th>Plano</th><th>Status</th><th>Permissão</th><th></th></tr></thead><tbody>
        {loading ? <tr><td colSpan="5">Carregando usuários…</td></tr> : filtered.map((item) => <tr key={item.id}><td><strong>{item.nome || "Sem nome"}</strong><small>{item.email}</small></td><td>{item.plano === "vitalicio" ? "Vitalício" : item.plano === "trimestral" ? "3 meses" : item.plano || "—"}</td><td><span className={`status-dot ${item.ativo === false ? "off" : ""}`}>{item.ativo === false ? "Bloqueado" : "Ativo"}</span></td><td>{item.is_admin ? "Super admin" : item.acessos ? "Personalizado" : "Segue o plano"}</td><td><div className="row-actions"><button onClick={() => open(item)}>Editar</button><button className="danger" onClick={() => remove(item)}>Excluir</button></div></td></tr>)}
      </tbody></table></div>
      {editing && <AdminModal wide title={editing.id ? "Editar usuário" : "Criar usuário"} onClose={() => setEditing(null)}><form className="admin-form" onSubmit={save}>
        <div className="form-grid"><label>Nome<input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required /></label><label>E-mail<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></label><label>{editing.id ? "Nova senha (opcional)" : "Senha inicial"}<input type="password" minLength="6" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing.id} /></label><label>Plano<select value={form.plano} onChange={(e) => changePlan(e.target.value)}><option value="trimestral">3 meses</option><option value="vitalicio">Vitalício</option></select></label><label>Expira em<input type="date" value={form.expira_em || ""} onChange={(e) => setForm({ ...form, expira_em: e.target.value })} disabled={form.plano === "vitalicio"} /></label></div>
        <section className={`user-access-panel ${form.acessos_personalizados ? "custom" : ""}`}>
          <header><div><span>Conteúdos liberados</span><small>{form.acessos_personalizados ? "Permissões exclusivas para este usuário" : `Aplicando automaticamente o plano ${form.plano === "vitalicio" ? "Vitalício" : "3 meses"}`}</small></div><label className="access-mode-toggle"><input type="checkbox" checked={form.acessos_personalizados} onChange={(event) => setCustomAccess(event.target.checked)} /><b>Personalizar acessos</b></label></header>
          <div className="user-feature-grid">{PLAN_FEATURES.map((feature) => <label className={effectiveAccess[feature.id] ? "enabled" : ""} key={feature.id}><input type="checkbox" checked={Boolean(effectiveAccess[feature.id])} disabled={!form.acessos_personalizados} onChange={() => toggleUserFeature(feature.id)} /><span><strong>{feature.label}</strong><small>{feature.description}</small></span><i>{effectiveAccess[feature.id] ? "Liberado" : "Bloqueado"}</i></label>)}</div>
        </section>
        <div className="toggle-list user-account-toggles"><label><input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Conta ativa</label><label><input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} /> Super administrador</label></div>
        <button className="admin-primary" disabled={busy}>{busy ? "Salvando…" : "Salvar usuário"}</button>
      </form></AdminModal>}
    </section>
  );
}

const PREMIUM_CATEGORY_ID = "b3ea1425-a352-4ade-9f10-97fdd86d23d9";
const CONTENT_LABELS = { categorias: "Categorias", temas: "Temas", figurinhas: "Figurinhas", premium: "Sombras & Molduras" };
const CONTENT_ITEM_LABELS = { categorias: "categoria", temas: "tema", figurinhas: "figurinha", premium: "item premium" };
const isStickerContent = (value) => value === "figurinhas" || value === "premium";
const apiContentKind = (value) => value === "premium" ? "figurinhas" : value;

function StickerPreview({ url, editing }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [url]);
  return (
    <section className="sticker-preview-panel" aria-live="polite">
      <header><span>Pré-visualização</span><i>{url && !failed ? (editing ? "Imagem atual" : "Nova imagem") : "Aguardando imagem"}</i></header>
      <div className="sticker-preview-stage">
        {url && !failed ? <img src={imageProxyUrl(originalImageUrl(url))} alt="Prévia da figurinha" onError={() => setFailed(true)} /> : <div className="sticker-preview-empty"><b>✦</b><strong>{failed ? "Não foi possível abrir a imagem" : "Sua figurinha aparece aqui"}</strong><small>{failed ? "Confira a URL ou envie outro arquivo." : "Envie um arquivo ou cole uma URL para visualizar."}</small></div>}
      </div>
      {url && !failed && <small className="sticker-preview-caption">A prévia é atualizada automaticamente antes de salvar.</small>}
    </section>
  );
}

function ContentManager({ notify }) {
  const [kind, setKind] = useState("figurinhas");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState("");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  async function load(nextKind = kind, themeId = selectedTheme) {
    setBusy(true);
    try {
      const [categoryRows, themeRows] = await Promise.all([adminApi.get("content/categorias"), adminApi.get("content/temas")]);
      const availableThemes = nextKind === "premium"
        ? themeRows.filter((theme) => theme.categoria_id === PREMIUM_CATEGORY_ID)
        : nextKind === "figurinhas"
          ? themeRows.filter((theme) => theme.categoria_id !== PREMIUM_CATEGORY_ID)
          : themeRows;
      const chosenTheme = isStickerContent(nextKind)
        ? (availableThemes.some((theme) => theme.id === themeId) ? themeId : availableThemes[0]?.id || "")
        : "";
      const rows = isStickerContent(nextKind)
        ? (chosenTheme ? await adminApi.get(`content/figurinhas?tema_id=${encodeURIComponent(chosenTheme)}`) : [])
        : await adminApi.get(`content/${nextKind}`);
      setItems(rows); setCategories(categoryRows); setThemes(themeRows);
      if (isStickerContent(nextKind) && chosenTheme !== selectedTheme) setSelectedTheme(chosenTheme);
    } catch (error) { notify(error.message, "error"); }
    setBusy(false);
  }
  useEffect(() => { load(kind, selectedTheme); }, [kind, selectedTheme]);

  const stickerMode = isStickerContent(kind);
  const themeChoices = kind === "premium"
    ? themes.filter((theme) => theme.categoria_id === PREMIUM_CATEGORY_ID)
    : themes.filter((theme) => theme.categoria_id !== PREMIUM_CATEGORY_ID);
  const visible = items.filter((item) => `${item.nome || ""} ${item.url || ""}`.toLowerCase().includes(query.toLowerCase()));
  function open(item) {
    const defaults = kind === "categorias" ? { nome: "", emoji: "✦", ordem: items.length + 1 } : kind === "temas" ? { nome: "", emoji: "✦", categoria_id: categories[0]?.id || "", total_figurinhas: 0 } : { tema_id: themeChoices[0]?.id || "", url: "", ordem: items.length + 1, ativo: true, destaque: false };
    setEditing(item || {}); setForm(item ? { ...item } : defaults);
  }
  async function save(event) {
    event.preventDefault(); setBusy(true);
    try {
      const payload = { ...form }; delete payload.id; delete payload.criado_em;
      const contentKind = apiContentKind(kind);
      if (editing?.id) await adminApi.patch(`content/${contentKind}/${editing.id}`, payload); else await adminApi.post(`content/${contentKind}`, payload);
      notify(`${kind === "premium" ? "Item premium" : kind === "figurinhas" ? "Figurinha" : kind === "temas" ? "Tema" : "Categoria"} ${editing?.id ? "atualizado(a)" : "adicionado(a)"}.`);
      setEditing(null); await load();
    } catch (error) { notify(error.message, "error"); } finally { setBusy(false); }
  }
  async function upload(event) {
    const file = event.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const result = await adminApi.upload(file); setForm((current) => ({ ...current, url: result.url })); notify("Imagem enviada para o Cloudflare R2."); } catch (error) { notify(error.message, "error"); }
    setBusy(false);
  }
  async function remove(item) {
    if (!window.confirm("Excluir este item definitivamente?")) return;
    try { await adminApi.remove(`content/${apiContentKind(kind)}/${item.id}`); notify("Conteúdo excluído."); await load(); } catch (error) { notify(error.message, "error"); }
  }
  async function toggleActive(item) {
    setBusy(true);
    try {
      const activate = item.ativo === false;
      await adminApi.patch(`content/figurinhas/${item.id}`, { ativo: activate });
      notify(activate ? "Conteúdo ativado e disponível." : "Conteúdo desativado e ocultado.");
      await load();
    } catch (error) { notify(error.message, "error"); } finally { setBusy(false); }
  }
  async function importCatalog(event) {
    event.preventDefault(); setBusy(true);
    try {
      const category = JSON.parse(importText);
      const result = await adminApi.post("import", { category });
      notify(`${result.category}: ${result.themes} temas e ${result.stickers} figurinhas importados.`);
      setImportText("");
      setKind("categorias");
      await load("categorias");
    } catch (error) { notify(error.message || "Arquivo de importação inválido.", "error"); } finally { setBusy(false); }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading"><div><h2>Biblioteca de conteúdo</h2><p>Organize todo o acervo publicado no aplicativo.</p></div><div className="admin-heading-actions"><button className="admin-secondary" onClick={() => setImporting(true)}>Importar acervo</button><button className="admin-primary" onClick={() => open(null)}>+ Adicionar</button></div></div>
      <div className="content-tabs">{Object.entries(CONTENT_LABELS).map(([id, label]) => <button className={`${kind === id ? "active" : ""} ${id === "premium" ? "premium-tab" : ""}`} key={id} onClick={() => { setKind(id); setSelectedTheme(""); setQuery(""); }}>{label}</button>)}</div>
      {kind === "premium" && <div className="premium-admin-summary"><span>PF</span><div><strong>Produto Vitalício</strong><small>Gerencie Sombras e Molduras separadamente do acervo de figurinhas.</small></div></div>}
      {stickerMode && <label className="admin-filter">{kind === "premium" ? "Grupo" : "Tema"}<select aria-label={kind === "premium" ? "Filtrar por Sombras ou Molduras" : "Filtrar figurinhas por tema"} value={selectedTheme} onChange={(event) => setSelectedTheme(event.target.value)}><option value="">Selecione</option>{themeChoices.map((theme) => <option key={theme.id} value={theme.id}>{theme.nome}</option>)}</select></label>}
      <label className="admin-search"><span>⌕</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Buscar em ${CONTENT_LABELS[kind].toLowerCase()}`} /></label>
      <div className="content-list">{busy && !items.length ? <p>Carregando conteúdo…</p> : visible.length ? visible.map((item) => <article key={item.id}>{stickerMode ? <img src={imageProxyUrl(item.url)} alt="Prévia" /> : <span>{item.emoji || "✦"}</span>}<div><strong>{item.nome || (themes.find((theme) => theme.id === item.tema_id)?.nome ?? "Figurinha")}</strong><small>{stickerMode ? item.url : item.id}</small></div>{stickerMode && <i className={item.ativo === false ? "off" : ""}>{item.ativo === false ? "Inativo" : "Ativo"}</i>}<div className="row-actions">{stickerMode && <button className={item.ativo === false ? "status-action activate" : "status-action deactivate"} onClick={() => toggleActive(item)} disabled={busy}>{item.ativo === false ? "Ativar" : "Desativar"}</button>}<button onClick={() => open(item)}>Editar</button><button className="danger" onClick={() => remove(item)}>Excluir</button></div></article>) : <div className="content-empty"><strong>Nenhum conteúdo encontrado</strong><small>{stickerMode ? "Adicione um arquivo neste grupo ou escolha outro filtro." : "Adicione o primeiro item para começar."}</small></div>}</div>
      {editing && <AdminModal wide={stickerMode} title={`${editing.id ? "Editar" : "Adicionar"} ${CONTENT_ITEM_LABELS[kind]}`} onClose={() => setEditing(null)}><form className="admin-form" onSubmit={save}>
        {!stickerMode && <div className="form-grid"><label>Nome<input value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} required /></label><label>Emoji<input value={form.emoji || ""} onChange={(e) => setForm({ ...form, emoji: e.target.value })} /></label>{kind === "categorias" && <label>Ordem<input type="number" value={form.ordem ?? 0} onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })} /></label>}{kind === "temas" && <><label>Categoria<select value={form.categoria_id || ""} onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}>{categories.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}</select></label><label>Total exibido<input type="number" value={form.total_figurinhas ?? 0} onChange={(e) => setForm({ ...form, total_figurinhas: Number(e.target.value) })} /></label></>}</div>}
        {stickerMode && <div className="sticker-editor-grid"><StickerPreview url={form.url || ""} editing={Boolean(editing.id)} /><div className="sticker-editor-fields"><label className="upload-zone"><input type="file" accept="image/png,image/webp,image/jpeg" onChange={upload} /><span>{form.url ? "Trocar imagem" : "Enviar PNG, WebP ou JPG"}</span><small>O arquivo será armazenado no Cloudflare R2.</small></label><div className="form-grid"><label>URL da imagem<input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} required /></label><label>{kind === "premium" ? "Grupo" : "Tema"}<select value={form.tema_id || ""} onChange={(e) => setForm({ ...form, tema_id: e.target.value })}>{themeChoices.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}</select></label><label>Ordem<input type="number" value={form.ordem ?? 0} onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })} /></label></div><div className="toggle-list"><label><input type="checkbox" checked={form.ativo !== false} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Conteúdo ativo</label><label><input type="checkbox" checked={Boolean(form.destaque)} onChange={(e) => setForm({ ...form, destaque: e.target.checked })} /> Destaque na página inicial</label></div></div></div>}
        <button className="admin-primary" disabled={busy}>{busy ? "Salvando…" : "Salvar conteúdo"}</button>
      </form></AdminModal>}
      {importing && <AdminModal title="Importar acervo" onClose={() => !busy && setImporting(false)}><form className="admin-form" onSubmit={importCatalog}><p className="admin-note">Cole o JSON de uma categoria. Temas e figurinhas existentes dessa categoria serão sincronizados.</p><label>Dados JSON<textarea aria-label="Dados JSON" value={importText} onChange={(event) => setImportText(event.target.value)} rows="10" required /></label><button className="admin-primary" disabled={busy}>{busy ? "Importando…" : "Importar categoria"}</button></form></AdminModal>}
    </section>
  );
}

function PlansManager({ settings, setSettings, notify }) {
  const [busy, setBusy] = useState(false);
  const plans = [
    ["trimestral", "3 meses", "Acesso por 90 dias após o pagamento"],
    ["vitalicio", "Vitalício", "Acesso permanente às funções selecionadas"],
  ];
  const features = normalizePlanFeatures(settings.plan_features);

  function toggle(plan, feature) {
    setSettings((current) => ({
      ...current,
      plan_features: {
        ...normalizePlanFeatures(current.plan_features),
        [plan]: { ...normalizePlanFeatures(current.plan_features)[plan], [feature]: !normalizePlanFeatures(current.plan_features)[plan][feature] },
      },
    }));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { ...settings, plan_features: features };
      const result = await adminApi.patch("settings", payload);
      setSettings(normalizeSettings(result));
      notify("Benefícios dos planos atualizados.");
    } catch (error) { notify(error.message, "error"); } finally { setBusy(false); }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading"><div><h2>Benefícios por plano</h2><p>Escolha exatamente o que cada compra libera no aplicativo.</p></div></div>
      <form className="plan-matrix" onSubmit={save}>
        {plans.map(([plan, title, description]) => <article className={`plan-access-card ${plan}`} key={plan}>
          <header><span>{plan === "vitalicio" ? "∞" : "90"}</span><div><strong>{title}</strong><small>{description}</small></div></header>
          <div className="feature-checklist">
            {PLAN_FEATURES.map((feature) => <label key={feature.id}><input type="checkbox" checked={Boolean(features[plan]?.[feature.id])} onChange={() => toggle(plan, feature.id)} /><span><strong>{feature.label}</strong><small>{feature.description}</small></span></label>)}
          </div>
        </article>)}
        <button className="admin-primary plan-save" disabled={busy}>{busy ? "Salvando…" : "Salvar benefícios dos planos"}</button>
      </form>
    </section>
  );
}

function SettingsForm({ mode, settings, setSettings, notify }) {
  const [busy, setBusy] = useState(false);
  const isPayments = mode === "payments";
  async function save(event) {
    event.preventDefault(); setBusy(true);
    try { const result = await adminApi.patch("settings", settings); setSettings({ ...DEFAULT_SETTINGS, ...result }); notify("Configurações publicadas."); } catch (error) { notify(error.message, "error"); }
    setBusy(false);
  }
  const field = (key, label, type = "text", placeholder = "") => <label>{label}<input type={type} value={settings[key] || ""} placeholder={placeholder} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} /></label>;
  if (isPayments) return (
    <section className="admin-section">
      <div className="admin-section-heading"><div><h2>Pagamentos e entrega</h2><p>Configure o checkout e o envio automático do acesso ao cliente.</p></div></div>
      <div className="integration-stack">
        <form className="settings-card admin-form" onSubmit={save}><div className="wiven-badge"><b>W</b><div><strong>Wiven Checkout</strong><small>Pagamento aprovado libera o plano automaticamente</small></div><span>Webhook ativo</span></div><div className="form-grid">{field("checkout_default", "Página principal de vendas", "url", "https://...")}{field("checkout_trimestral", "Checkout 3 meses", "url")}{field("checkout_vitalicio", "Checkout vitalício", "url")}{field("wiven_offer_trimestral", "Código da oferta · 3 meses")}{field("wiven_offer_vitalicio", "Código da oferta · Vitalício")}</div><p className="admin-note">Cole os códigos das ofertas exatamente como aparecem na Wiven. O webhook identifica a compra, cria ou atualiza o cliente e aplica os benefícios configurados na aba Planos.</p><button className="admin-primary" disabled={busy}>{busy ? "Publicando…" : "Salvar Wiven"}</button></form>
        <EvolutionSettings notify={notify} />
        <GmailSettings notify={notify} />
      </div>
    </section>
  );
  return (
    <section className="admin-section"><div className="admin-section-heading"><div><h2>Identidade do site</h2><p>Altere textos, marca, cores e canais de suporte.</p></div></div>
      <form className="settings-card admin-form" onSubmit={save}><div className="form-grid">{field("brand_name", "Nome do site")}{field("tagline", "Frase da marca")}{field("logo_url", "URL do logo", "url")}{field("accent", "Cor principal", "color")}{field("banner_title", "Título do banner")}{field("banner_subtitle", "Texto do banner")}{field("support_email", "E-mail de suporte", "email")}{field("support_whatsapp", "WhatsApp com DDD")}</div><div className="brand-preview" style={{ "--preview-accent": settings.accent }}><span>{settings.logo_url ? <img src={settings.logo_url} alt="" /> : (settings.brand_name || "P").charAt(0)}</span><div><strong>{settings.brand_name}</strong><small>{settings.tagline}</small></div></div><button className="admin-primary" disabled={busy}>{busy ? "Publicando…" : "Publicar alterações"}</button></form>
    </section>
  );
}

function EvolutionSettings({ notify }) {
  const [form, setForm] = useState({ url: "", instance: "", api_key: "", api_key_configured: false, enabled: true });
  const [busy, setBusy] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [instanceBusy, setInstanceBusy] = useState("");
  const [connection, setConnection] = useState(null);
  const [qrCode, setQrCode] = useState("");

  useEffect(() => {
    adminApi.get("integrations/evolution")
      .then(async (data) => {
        setForm((current) => ({ ...current, ...data, api_key: "" }));
        setDirty(false);
        if (data.api_key_configured && data.instance) {
          try { setConnection(await adminApi.post("integrations/evolution/test", {})); } catch { setConnection({ connected: false, state: "indisponível" }); }
        }
      })
      .catch((error) => notify(error.message, "error"))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    if (!qrCode) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const result = await adminApi.post("integrations/evolution/test", {});
        setConnection(result);
        if (result.connected) {
          setQrCode("");
          notify("WhatsApp conectado e pronto para enviar.");
        }
      } catch { /* O QR continua visível para uma nova tentativa manual. */ }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [qrCode]);

  function change(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setConnection(null);
    setQrCode("");
  }

  function useLifecycle(result) {
    setConnection(result);
    setQrCode(result.qr_code || "");
  }

  async function save(event) {
    event.preventDefault(); setBusy(true); setConnection(null);
    try {
      const result = await adminApi.patch("integrations/evolution", { url: form.url, instance: form.instance, api_key: form.api_key, enabled: form.enabled });
      setForm((current) => ({ ...current, ...result, api_key: "" }));
      setDirty(false);
      setQrCode("");
      notify("Evolution API configurada com segurança.");
    } catch (error) { notify(error.message, "error"); } finally { setBusy(false); }
  }

  async function testConnection() {
    setInstanceBusy("status");
    try {
      const result = await adminApi.post("integrations/evolution/test", {});
      useLifecycle(result);
      notify(result.connected ? "WhatsApp conectado e pronto para enviar." : `Evolution respondeu: ${result.state}.`, result.connected ? "success" : "error");
    } catch (error) { notify(error.message, "error"); setConnection({ connected: false, state: "erro" }); } finally { setInstanceBusy(""); }
  }

  async function createInstance() {
    setInstanceBusy("create"); setConnection(null); setQrCode("");
    try {
      const result = await adminApi.post("integrations/evolution/instance", {});
      useLifecycle(result);
      notify(result.connected ? "Instância criada e conectada." : "Instância criada. Escaneie o QR Code com o WhatsApp.");
    } catch (error) { notify(error.message, "error"); } finally { setInstanceBusy(""); }
  }

  async function connectInstance() {
    setInstanceBusy("connect"); setQrCode("");
    try {
      const result = await adminApi.post("integrations/evolution/connect", {});
      useLifecycle(result);
      notify(result.connected ? "WhatsApp já está conectado." : result.qr_code ? "Novo QR Code gerado." : `Estado da instância: ${result.state}.`);
    } catch (error) { notify(error.message, "error"); } finally { setInstanceBusy(""); }
  }

  async function disconnectInstance() {
    if (!window.confirm("Desconectar este WhatsApp da Evolution API? Você poderá gerar um novo QR Code depois.")) return;
    setInstanceBusy("disconnect");
    try {
      const result = await adminApi.post("integrations/evolution/disconnect", {});
      useLifecycle(result);
      notify("WhatsApp desconectado.");
    } catch (error) { notify(error.message, "error"); } finally { setInstanceBusy(""); }
  }

  const canManage = form.enabled && form.api_key_configured && !dirty && !busy;
  const stateLabel = connection?.connected ? "Conectado" : connection?.state === "close" ? "Desconectado" : connection?.state === "connecting" ? "Aguardando leitura" : connection?.state === "created" ? "Criada" : connection?.state || "Não verificado";

  return (
    <form className="settings-card admin-form evolution-card" onSubmit={save}>
      <div className="evolution-badge"><b>WA</b><div><strong>Evolution API</strong><small>Entrega login e senha temporária pelo WhatsApp</small></div><span className={form.enabled && form.api_key_configured ? "ready" : ""}>{form.enabled && form.api_key_configured ? "Configurada" : "Pendente"}</span></div>
      <div className="form-grid"><label>URL da Evolution API<input type="url" value={form.url} placeholder="https://evolution.seudominio.com" onChange={(event) => change("url", event.target.value)} required={form.enabled} /><small className="field-help">Use o endereço do servidor. Se você colar a URL com /manager, o sistema removerá essa parte automaticamente.</small></label><label>Nome da instância<input value={form.instance} placeholder="packfigure" pattern="[a-zA-Z0-9_-]{2,60}" title="Use apenas letras, números, hífen ou sublinhado" onChange={(event) => change("instance", event.target.value)} required={form.enabled} /></label><label>Chave global da API<input type="password" value={form.api_key} autoComplete="new-password" placeholder={form.api_key_configured ? "Chave salva — preencha apenas para trocar" : "Cole a API key"} onChange={(event) => change("api_key", event.target.value)} required={form.enabled && !form.api_key_configured} /></label></div>
      <div className="toggle-list"><label><input type="checkbox" checked={form.enabled} onChange={(event) => change("enabled", event.target.checked)} /> Envio automático ativo</label></div>
      <p className="admin-note secure">A chave é guardada em uma tabela privada do Supabase. Ela nunca é enviada ao navegador novamente nem aparece para os clientes.</p>
      <section className="instance-manager">
        <header><div><small>INSTÂNCIA DO WHATSAPP</small><strong>{form.instance || "Defina um nome acima"}</strong></div><span className={connection?.connected ? "connected" : ""}><i />{stateLabel}</span></header>
        {dirty && <p className="instance-hint">Salve as alterações antes de criar ou controlar a instância.</p>}
        {qrCode && <div className="evolution-qr"><img src={qrCode} alt="QR Code para conectar o WhatsApp" /><div><strong>Escaneie pelo WhatsApp</strong><span>Abra WhatsApp → Aparelhos conectados → Conectar um aparelho.</span>{connection?.pairing_code && <code>{connection.pairing_code}</code>}<small>O status é atualizado automaticamente enquanto este QR estiver aberto.</small></div></div>}
        <div className="instance-actions"><button className="admin-secondary" type="button" onClick={createInstance} disabled={!canManage || Boolean(instanceBusy) || connection?.connected}>{instanceBusy === "create" ? "Criando…" : "Criar instância"}</button><button className="admin-secondary" type="button" onClick={connectInstance} disabled={!canManage || Boolean(instanceBusy) || connection?.connected}>{instanceBusy === "connect" ? "Gerando…" : "Gerar novo QR"}</button><button className="admin-secondary" type="button" onClick={testConnection} disabled={!canManage || Boolean(instanceBusy)}>{instanceBusy === "status" ? "Consultando…" : "Atualizar status"}</button>{connection?.connected && <button className="admin-secondary disconnect" type="button" onClick={disconnectInstance} disabled={Boolean(instanceBusy)}>{instanceBusy === "disconnect" ? "Desconectando…" : "Desconectar"}</button>}</div>
      </section>
      <div className="integration-actions"><button className="admin-primary" disabled={busy}>{busy ? "Salvando…" : "Salvar Evolution API"}</button></div>
    </form>
  );
}

function GmailSettings({ notify }) {
  const [form, setForm] = useState({ method: "app_password", sender_name: "PackFigure", sender_email: "", app_password: "", app_password_configured: false, client_id: "", client_secret: "", client_secret_configured: false, connected: false, enabled: true, redirect_uri: "" });
  const [busy, setBusy] = useState(true);
  const [action, setAction] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminApi.get("integrations/gmail")
      .then((data) => { setForm((current) => ({ ...current, ...data, app_password: "", client_secret: "" })); setDirty(false); })
      .catch((error) => notify(error.message, "error"))
      .finally(() => setBusy(false));
  }, []);

  function change(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  }

  async function save(event) {
    event?.preventDefault();
    setBusy(true);
    try {
      const result = await adminApi.patch("integrations/gmail", {
        method: form.method,
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        app_password: form.app_password,
        client_id: form.client_id,
        client_secret: form.client_secret,
        enabled: form.enabled,
      });
      setForm((current) => ({ ...current, ...result, app_password: "", client_secret: "" }));
      setDirty(false);
      notify("Configuração do Gmail salva com segurança.");
      return result;
    } catch (error) {
      notify(error.message, "error");
      return null;
    } finally { setBusy(false); }
  }

  async function connect() {
    setAction("connect");
    try {
      if (dirty) {
        const saved = await save();
        if (!saved) return;
      }
      const result = await adminApi.post("integrations/gmail/authorize", {});
      window.location.assign(result.authorization_url);
    } catch (error) { notify(error.message, "error"); } finally { setAction(""); }
  }

  async function test() {
    setAction("test");
    try { await adminApi.post("integrations/gmail/test", {}); notify(`E-mail de teste enviado para ${form.sender_email}.`); }
    catch (error) { notify(error.message, "error"); }
    finally { setAction(""); }
  }

  async function disconnect() {
    if (!window.confirm("Desconectar a conta Gmail? O WhatsApp continuará funcionando normalmente.")) return;
    setAction("disconnect");
    try {
      const result = await adminApi.post("integrations/gmail/disconnect", {});
      setForm((current) => ({ ...current, ...result, app_password: "", client_secret: "" }));
      notify("Gmail desconectado.");
    } catch (error) { notify(error.message, "error"); }
    finally { setAction(""); }
  }

  async function copyRedirect() {
    try { await navigator.clipboard.writeText(form.redirect_uri); notify("URL de redirecionamento copiada."); }
    catch { notify("Não foi possível copiar. Selecione a URL manualmente.", "error"); }
  }

  const configured = form.method === "app_password"
    ? Boolean(form.sender_email && form.app_password_configured)
    : Boolean(form.sender_email && form.client_id && form.client_secret_configured);
  return (
    <form className="settings-card admin-form gmail-card" onSubmit={save}>
      <div className="gmail-badge"><b>G</b><div><strong>Gmail · envio por e-mail</strong><small>Entrega o mesmo login e senha temporária após a compra</small></div><span className={form.connected && form.enabled ? "ready" : ""}>{form.connected && form.enabled ? (form.method === "app_password" ? "Configurado" : "Conectado") : "Pendente"}</span></div>
      <div className="gmail-methods">
        <button className={form.method === "app_password" ? "active" : ""} type="button" onClick={() => change("method", "app_password")}><b>Senha de app</b><small>Mais rápido · Gmail + código de 16 caracteres</small></button>
        <button className={form.method === "oauth" ? "active" : ""} type="button" onClick={() => change("method", "oauth")}><b>OAuth do Google</b><small>Autorização oficial sem guardar senha</small></button>
      </div>
      <div className="gmail-layout">
        <div className="form-grid">
          <label>Nome do remetente<input value={form.sender_name} placeholder="PackFigure" onChange={(event) => change("sender_name", event.target.value)} required={form.enabled} /></label>
          <label>Gmail remetente<input type="email" value={form.sender_email} placeholder="contato@gmail.com" onChange={(event) => change("sender_email", event.target.value)} required={form.enabled} /></label>
          {form.method === "app_password" ? <label className="wide-field">Senha de app do Gmail<input type="password" autoComplete="new-password" value={form.app_password} placeholder={form.app_password_configured ? "Senha salva — preencha apenas para trocar" : "Cole os 16 caracteres"} onChange={(event) => change("app_password", event.target.value)} required={form.enabled && !form.app_password_configured} /><small className="field-help">Pode colar com ou sem espaços. Não use a senha normal da sua conta.</small></label> : <><label>OAuth Client ID<input value={form.client_id} placeholder="000000000000-….apps.googleusercontent.com" onChange={(event) => change("client_id", event.target.value)} required={form.enabled} /></label><label>OAuth Client Secret<input type="password" autoComplete="new-password" value={form.client_secret} placeholder={form.client_secret_configured ? "Segredo salvo — preencha apenas para trocar" : "Cole o Client Secret"} onChange={(event) => change("client_secret", event.target.value)} required={form.enabled && !form.client_secret_configured} /></label></>}
        </div>
        <aside className="gmail-setup">
          {form.method === "app_password" ? <><strong>Como gerar a senha de app</strong><ol><li>Ative a verificação em duas etapas na conta Google.</li><li>Abra “Senhas de app” e crie uma para PackFigure.</li><li>Cole no campo ao lado o código de 16 caracteres.</li></ol><a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">Gerar senha de app no Google ↗</a></> : <><strong>Configuração no Google Cloud</strong><ol><li>Ative a Gmail API.</li><li>Crie um cliente OAuth do tipo Aplicativo da Web.</li><li>Adicione a URL abaixo em “URIs de redirecionamento autorizados”.</li></ol><div className="oauth-redirect"><code>{form.redirect_uri || "Carregando URL…"}</code><button type="button" onClick={copyRedirect} disabled={!form.redirect_uri}>Copiar</button></div><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">Abrir credenciais do Google Cloud ↗</a></>}
        </aside>
      </div>
      <div className="toggle-list"><label><input type="checkbox" checked={form.enabled} onChange={(event) => change("enabled", event.target.checked)} /> Envio automático por e-mail ativo</label></div>
      <p className="admin-note secure">{form.method === "app_password" ? "A senha de app é armazenada na tabela privada do Supabase, nunca reaparece no navegador e pode ser revogada a qualquer momento na conta Google." : "O Gmail é conectado pela autorização oficial do Google. O segredo e o token ficam na tabela privada do Supabase e nunca aparecem para clientes."}</p>
      <div className="gmail-actions">
        {form.method === "oauth" && <button className="admin-secondary" type="button" onClick={connect} disabled={busy || Boolean(action) || (!configured && !dirty)}>{action === "connect" ? "Abrindo Google…" : form.connected ? "Reconectar Gmail" : "Conectar Gmail"}</button>}
        <button className="admin-secondary" type="button" onClick={test} disabled={busy || Boolean(action) || !form.connected || !form.enabled}>{action === "test" ? "Enviando…" : "Enviar teste"}</button>
        {form.connected && <button className="admin-secondary disconnect" type="button" onClick={disconnect} disabled={Boolean(action)}>{action === "disconnect" ? "Desconectando…" : "Desconectar"}</button>}
        <button className="admin-primary" disabled={busy || Boolean(action)}>{busy ? "Salvando…" : "Salvar Gmail"}</button>
      </div>
    </form>
  );
}

function AdminModal({ title, onClose, children, wide = false }) {
  return <div className="admin-modal-backdrop" onMouseDown={onClose}><section className={`admin-modal ${wide ? "wide" : ""}`} onMouseDown={(e) => e.stopPropagation()}><header><h2>{title}</h2><button type="button" onClick={onClose}>×</button></header>{children}</section></div>;
}
